import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { authService } from '@services/authService'
import { determineAppType } from '@utils/appDetection'

// Auth Context
const AuthContext = createContext()

// Auth Actions
const AUTH_ACTIONS = {
  LOADING: 'LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  ERROR: 'ERROR'
}

// Auth Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOADING:
      return {
        ...state,
        loading: action.payload,
        error: null
      }
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        userType: action.payload.userType,
        loading: false,
        error: null
      }
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        userType: null,
        loading: false,
        error: null
      }
    
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
        error: null
      }
    
    case AUTH_ACTIONS.ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      }
    
    default:
      return state
  }
}

// Initial State
const initialState = {
  isAuthenticated: false,
  user: null,
  token: null,
  userType: null,
  loading: true,
  error: null
}

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const appType = determineAppType()

  // Initialize auth state on app load
  useEffect(() => {
    initializeAuth()
  }, [appType])

  const initializeAuth = async () => {
    dispatch({ type: AUTH_ACTIONS.LOADING, payload: true })
    
    try {
      const token = authService.getToken(appType)
      
      if (token) {
        // Verify token and get user info
        const userData = await authService.getCurrentUser(appType)
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: userData.user,
            token: token,
            userType: appType
          }
        })
      } else {
        dispatch({ type: AUTH_ACTIONS.LOADING, payload: false })
      }
    } catch (error) {
      console.error('Auth initialization failed:', error)
      authService.removeToken(appType)
      dispatch({ type: AUTH_ACTIONS.LOADING, payload: false })
    }
  }

  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOADING, payload: true })
    
    try {
      const response = await authService.login(credentials, appType)
      
      // Store token
      authService.setToken(response.token, appType)
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: response.user,
          token: response.token,
          userType: appType
        }
      })
      
      return response
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.ERROR,
        payload: error.message || 'Login failed'
      })
      throw error
    }
  }

  const logout = async () => {
    dispatch({ type: AUTH_ACTIONS.LOADING, payload: true })
    
    try {
      await authService.logout(appType)
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      authService.removeToken(appType)
      dispatch({ type: AUTH_ACTIONS.LOGOUT })
    }
  }

  const updateUser = (userData) => {
    dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: userData })
  }

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.ERROR, payload: null })
  }

  // Context value
  const value = {
    ...state,
    appType,
    login,
    logout,
    updateUser,
    clearError,
    refreshAuth: initializeAuth
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  
  return context
}

// Hook for specific user types
export const useSuperAdminAuth = () => {
  const auth = useAuth()
  
  if (auth.appType !== 'super-admin') {
    throw new Error('useSuperAdminAuth can only be used in super admin context')
  }
  
  return auth
}

export const useStateOfficialAuth = () => {
  const auth = useAuth()
  
  return {
    ...auth,
    isStateOfficial: auth.userType === 'state-official',
    status: auth.user?.status || 0
  }
}

export const useClientAdminAuth = () => {
  const auth = useAuth()
  
  return {
    ...auth,
    isClientAdmin: auth.userType === 'tenant-admin',
    tenant: auth.user?.tenant
  }
}